import React from 'react';
import StaffProfileTemplate, {
  ProfileSection,
  ProfileParagraphs,
  ProfileBullets,
  ProfilePublications,
  ProfileContactSection,
} from './StaffProfileTemplate';

const KevideLchoProfile = () => {
  const person = {
    id: 'kevide-lcho',
    name: 'Kevide Lcho',
    honorific: 'Mr',
    title: 'Assistant Researcher',
    affiliation: 'The Highland Institute, Kohima, Nagaland',
    image: 'https://customer-assets.emergentagent.com/job_vegr-highland/artifacts/j8ony0is_Kevide%20Lcho.jpg',
    email: 'kevide@highlandinstitute.org',
    category: 'Research Staff',
    testid: 'kevide-lcho-profile',
  };

  return (
    <StaffProfileTemplate person={person}>
      <ProfileSection title="Research Biography">
        <ProfileParagraphs
          items={[
            'Kevide is an Assistant Researcher at The Highland Institute, where his work engages with Indigenous knowledge systems, oral histories, and the cultural, environmental, and health-related histories of Northeast India. Grounded in long-term fieldwork in Eastern Nagaland and the Naga–Myanmar borderlands, his research examines how memory, landscape, material culture, and lived experience shape historical and community knowledge.',
            'His recent work includes participation in a three-year IDRC Canada-funded project on Traditional Ecological Knowledge and Climate Resilience in Eastern Nagaland, which explored community-based understandings of environmental change and sustainability through oral narratives and lived ecological practices. He has also contributed to health-related research, including a study on HIV infection among children aged 2–14 in Kohima District, Nagaland, covering the period 2017–2021, and work on a motorbike outreach innovation for people living with HIV in Noklak District. In 2023, he participated in a University of Göttingen project assessing the effects of recent changes in cooking methods and diets on the health outcomes of tribal communities in Nagaland.',
            <>
              He is currently part of the <strong>"War Residues: Repurposed Objects and Memories of a Global War along the Highland Naga–Myanmar Border"</strong> initiative, supported by the Delta on the Move Foundation. The project examines how World War II is remembered in borderland communities through oral histories and the continued presence of repurposed wartime materials, including aircraft fragments and military objects transformed into everyday tools. It foregrounds local agency in reworking wartime remnants and challenges dominant historical narratives that have often marginalised Indigenous and borderland experiences.
            </>,
            'Through ethnographic fieldwork in remote areas and collaboration with local knowledge holders, the project documents memories and narratives that are increasingly at risk of being lost as older generations pass. In doing so, it preserves material and oral histories while contributing to a more grounded and inclusive understanding of World War II in the region.',
            <>
              In addition to his research, Kevide has contributed to various collaborative cultural initiatives at The Highland Institute. For example, he was involved in the documentary <em>Healing Hands: Tale of an Angami Healer</em>, developed in collaboration with the School of Film, Media and Creative Arts at Rashtreeya Vidyalaya University. Screened at the Berlin International Film Festival in July 2025, the documentary explores Indigenous healing traditions among the Angami community, with particular attention to herbal knowledge, spirituality, faith, and relationships with the natural world.
            </>,
            "Across these areas, Kevide's broader intellectual interest lies in how communities remember and rework historical experience through everyday practices, oral narratives, landscapes, health practices, and material objects. He is particularly interested in how wartime remnants and ecological knowledge become embedded in local lifeworlds, shaping understandings of identity, place, and historical change in communities often marginalised in dominant historiographies.",
          ]}
        />
        <p className="text-stone-700 font-medium mt-6 mb-4">Key areas of interest include:</p>
        <ProfileBullets
          items={[
            'Oral history and memory studies in borderland contexts',
            'Material culture and repurposed wartime objects',
            'World War II histories in Nagaland (India) and the India–Myanmar borderlands',
            'Indigenous knowledge systems and Traditional Ecological Knowledge (TEK)',
            'Climate resilience and environmental change in Eastern Nagaland',
            'Community-based and participatory ethnographic research',
            'Landscape, place, and historical imagination',
            'Cultural documentation and heritage preservation',
          ]}
        />
      </ProfileSection>

      <ProfileSection title="Selected Publications & Knowledge Outputs">
        <ProfilePublications
          items={[
            {
              citation: (
                <>
                  Katiry, T., Lcho, K., Wonti, S., & Child, C. (2024). Earthkeepers Project: Noklak Border Mission. <em>Highlander</em>, 3(2), 116–124.
                </>
              ),
              url: 'https://doi.org/10.5281/zenodo.11002249',
            },
            {
              citation: (
                <>
                  Lcho, K., Wonti, S., & Katiry, T. (2025). Traditional ecological knowledge and climate resilience in Eastern Nagaland: Insights from the Earthkeepers Project 2023–2025 [Unpublished report]. Earthkeepers Project, The Highland Institute.
                </>
              ),
            },
          ]}
        />
        <h3 className="font-semibold text-stone-900 mt-8 mb-3 not-prose">Blogs</h3>
        <ul className="space-y-3 not-prose">
          <li className="text-stone-700 pl-4 border-l-2 border-green-600">
            <a
              href="https://borderlab.eu/blog/sleep-in-india-reap-in-myanmar-how-villagers-defy-division-on-a-porous-border/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-700 hover:text-green-800 underline break-all"
            >
              Sleep in India; Reap in Myanmar: how villagers defy division on a porous border
            </a>
          </li>
          <li className="text-stone-700 pl-4 border-l-2 border-green-600">
            <a
              href="https://blogs.highlanderpress.org/index.php/2026/02/06/the-oil-palm-dilemma-its-a-catch-22/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-700 hover:text-green-800 underline break-all"
            >
              The Oil Palm Dilemma: It's a Catch-22
            </a>
          </li>
          <li className="text-stone-700 pl-4 border-l-2 border-green-600">
            <a
              href="https://blogs.highlanderpress.org/index.php/2026/02/09/discovering-pineapple-paradise-ruzaphema-village/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-700 hover:text-green-800 underline break-all"
            >
              Discovering Pineapple Paradise: Ruzaphema Village
            </a>
          </li>
        </ul>
      </ProfileSection>

      <ProfileSection title="Postgraduate Qualification">
        <ProfileBullets items={['M.Sc. in Agriculture']} />
      </ProfileSection>

      <ProfileContactSection
        email={person.email}
        role="Assistant Researcher, The Highland Institute"
        testidPrefix="kevide"
        scholarlyProfiles={[
          { label: 'LinkedIn', url: 'https://www.linkedin.com/in/kevide-lcho-2b1b433b6', testid: 'kevide-linkedin-link' },
        ]}
      />
    </StaffProfileTemplate>
  );
};

export default KevideLchoProfile;
